import { useState, type FC } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { Alert, AlertType, GenericError, ProgressBar } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { EnrollmentHYCSLayout } from '@egw/layouts'
import { sendEvent } from '@egw/events'
import { CheckIcon } from '@heroicons/react/24/outline'
import { useGetEnrollmentVerifyPhoneData, useSubmitEnrollmentVerifyPhoneLinkData } from '@egw/api'
import { PageHeader } from '../page-header/page-header'
import { useValidateRoute } from '../../hooks/navigation/validate-route'
import { EDataLayerEventWealth } from '../../constants/events/EDataLayerEventWealth'
import { formatPhoneNumber } from '../../utils/phone-number/phone-number'

const log = new Logger('Enrollment - HYCS - VerifyPhone')

export const VerifyPhoneLinkV2: FC = () => {
  const { t } = useTranslation('features-enrollment')

  const isSecondPerson = false // Link to BE when available

  const { data, isLoading, error } = useGetEnrollmentVerifyPhoneData({ triggerPhoneMagicLink: true })
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentVerifyPhoneLinkData()

  const [codeResent, setCodeResent] = useState(false)
  const [counter, setCounter] = useState(0)

  const phoneNumber = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData.phoneNumber : ''
  const applicationId = data?.__typename === 'SubmitEnrollmentSuccess' ? data.enrollmentData.applicationId : ''

  // Protect route
  useValidateRoute(data?.path)

  const resendOtp = async (): Promise<void> => {
    setCodeResent(true)

    try {
      const response = await mutate({
        triggerPhoneMagicLink: true,
      })

      sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONEOTPRESEND_SUBMIT, {
        applicationId,
      })

      if (response?.__typename === 'SubmitEnrollmentSuccess') {
        sendEvent(EDataLayerEventWealth.BANKHYCHYS_PHONE_VERIFIED)
      }

      setTimeout(() => {
        setCounter(30)
        const intervalId = setInterval(() => {
          setCounter((prev) => {
            if (prev <= 1) {
              clearInterval(intervalId)
              setCodeResent(false)

              return 0
            }
            return prev - 1
          })
        }, 1000)
      }, 5000)
    } catch (e) {
      log.error('Error submitting form', e)
    }
  }

  return (
    <EnrollmentHYCSLayout title={t('common.verify_phone_v2.link.page_title')}>
      <div className="egw-container-enroll-hycs h-full flex flex-col relative md:justify-center justify-start items-start">
        {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
        {isLoading ? (
          <div className="w-full">
            <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
              <Skeleton count={1} height="100px" className="mb-10" />
              <Skeleton count={5} height="50px" />
            </SkeletonTheme>
          </div>
        ) : null}

        {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
        {!isLoading && !error ? (
          <div className="w-full flex-1 flex flex-col justify-start lg:justify-center">
            <PageHeader title={t('common.verify_phone_v2.link.title')} />
            {!isSecondPerson && (
              <ProgressBar showAnimation progressPercentage={data?.enrollmentData.progress} className="mb-md" />
            )}
            <p className={`typo-primary-base-regular text-text-meta mb-3xs ${!isSecondPerson ? 'mt-xl' : 'mt-md'}`}>
              {t('common.verify_phone_v2.link.sent_to')}
            </p>
            <p className="typo-primary-base-semibold text-text-header mb-md fs-exclude">
              {formatPhoneNumber(phoneNumber)}
            </p>

            <p className="typo-primary-base-regular text-text-meta mb-md">
              {t('common.verify_phone_v2.link.check_messages')}
            </p>

            <div className="flex items-baseline gap-4">
              <button
                onClick={() => void resendOtp()}
                disabled={codeResent || isSubmitting}
                className={`typo-primary-sm-semibold text-left w-fit ${codeResent ? 'text-gray-gray-600' : ' text-text-action'}`}
                type="button"
              >
                {t('common.verify_phone_v2.link.resend_message')}
              </button>
              {codeResent && !counter ? (
                <p className="typo-primary-sm-semibold text-text-header-success flex items-center gap-2">
                  {t('common.verify_phone_v2.link.message_sent')} <CheckIcon className="size-5" />
                </p>
              ) : null}
            </div>

            {counter ? (
              <p className="typo-primary-sm-semibold text-text-placeholder mt-2xs">
                {t('common.verify_phone_v2.link.send_another_message', {
                  time: counter,
                })}
              </p>
            ) : null}

            <div className="flex-1 md:hidden" />

            {submitError ? <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" /> : null}

            {/* AUTO GENERATED COMPONENTS */}
          </div>
        ) : null}

        {/* This error handles the case when the initial data is not loaded, for example network error */}
        {error ? (
          <div className="flex-1 flex flex-col justify-start md:justify-center">
            <GenericError errorMessage={t('common.generic_error')} />
          </div>
        ) : null}
      </div>
    </EnrollmentHYCSLayout>
  )
}
